import React, { useState } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaTrashAlt, FaPlus, FaTimes, FaStar } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageTestimonials() {
  const { testimonials, setTestimonials } = useWeb();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    review: ''
  });

  const openAddModal = () => {
    setFormData({
      name: '',
      location: '',
      rating: 5,
      review: ''
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    setTestimonials(prev => prev.filter(t => t.id !== confirmDeleteId));
    toast.success('Testimonial removed!');
    setConfirmDeleteId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.location || !formData.review) {
      toast.error('Please fill in Name, Location, and Review description');
      return;
    }

    const newTestimonial = {
      id: `t-${Date.now()}`,
      name: formData.name,
      location: formData.location,
      rating: Number(formData.rating),
      review: formData.review
    };

    setTestimonials(prev => [...prev, newTestimonial]);
    toast.success('Testimonial added successfully!');
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Manage Testimonials</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">Review, add, or delete client feedbacks displayed on the landing page.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl transition text-xs sm:text-sm"
        >
          <FaPlus />
          <span>Add Testimonial</span>
        </button>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((test) => (
          <div key={test.id} className="bg-slate-50 border rounded-2xl p-6 relative flex flex-col justify-between shadow-sm">
            <button 
              onClick={() => handleDelete(test.id)}
              className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition"
              title="Delete Review"
            >
              <FaTrashAlt />
            </button>

            <div className="space-y-3">
              <div className="flex text-amber-400 text-sm">
                {Array.from({ length: test.rating }).map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="text-slate-600 text-xs sm:text-sm italic leading-relaxed">
                "{test.review}"
              </p>
            </div>

            <div className="pt-4 border-t mt-6 flex justify-between items-center">
              <div>
                <h4 className="text-sm font-bold text-slate-900">{test.name}</h4>
                <p className="text-slate-400 text-[10px]">{test.location}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl p-6 sm:p-8 space-y-6">
            
            <div className="flex justify-between items-center border-b pb-4">
              <h3 className="text-lg font-bold text-slate-900">Add Testimonial Review</h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 focus:outline-none"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Customer Name *</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="E.g., Anil Kumar"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Location *</label>
                  <input 
                    type="text" 
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                    placeholder="E.g. Madhapur, Hyderabad"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Rating Stars *</label>
                  <select 
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:border-orange-500 outline-none"
                    required
                  >
                    <option value={5}>5 Stars (Excellent)</option>
                    <option value={4}>4 Stars (Good)</option>
                    <option value={3}>3 Stars (Average)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Review Message *</label>
                <textarea 
                  rows="4" 
                  value={formData.review}
                  onChange={(e) => setFormData({...formData, review: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none resize-none"
                  placeholder="Paste customer message or review feedback details here..."
                  required
                ></textarea>
              </div>

              <div className="pt-4 border-t flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setModalOpen(false)}
                  className="border border-slate-200 rounded-xl px-5 py-2.5 font-semibold text-slate-650 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-2.5 rounded-xl transition"
                >
                  Save Testimonial
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FaTrashAlt className="text-red-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete Testimonial?</h3>
            <p className="text-slate-500 text-sm">This customer review will be permanently removed from the website.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 border border-slate-200 rounded-xl py-2.5 font-semibold text-slate-700 hover:bg-slate-50 transition">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl transition">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
